import React, { useState, useEffect } from 'react';
import { getCharges, createCharge, updateCharge, deleteCharge, getParametrage, getProjets } from '../services/api';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiX } from 'react-icons/fi';

const formatMontant = (n: number) => new Intl.NumberFormat('fr-FR').format(n) + ' FCFA';

const Charges = () => {
  const [charges, setCharges] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [totalMontant, setTotalMontant] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [typecharges, setTypecharges] = useState<any[]>([]);
  const [projets, setProjets] = useState<any[]>([]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({ libelle: '', datecharge: '', typecharge_id: '', prixunitaire: '', quantite: '', projet_id: '', description: '' });

  const loadData = () => {
    getCharges({ page, limit: 50 }).then(r => {
      setCharges(r.data.data);
      setTotal(r.data.total);
      setTotalMontant(r.data.totalMontant || 0);
      setTotalPages(r.data.totalPages);
    }).catch(() => {});
  };

  useEffect(() => { loadData(); }, [page]);
  useEffect(() => {
    getParametrage('typecharges').then(r => setTypecharges(r.data)).catch(() => {});
    getProjets().then(r => setProjets(r.data)).catch(() => {});
  }, []);

  const resetForm = () => setForm({ libelle: '', datecharge: '', typecharge_id: '', prixunitaire: '', quantite: '', projet_id: '', description: '' });

  const openAdd = () => { resetForm(); setEditingId(''); setShowModal(true); };
  const openEdit = (c: any) => {
    setForm({
      libelle: c.libelle || '',
      datecharge: c.datecharge ? c.datecharge.substring(0, 10) : '',
      typecharge_id: c.typecharge_id || '',
      prixunitaire: c.prixunitaire || '',
      quantite: c.quantite || '',
      projet_id: c.projet_id || '',
      description: c.description || ''
    });
    setEditingId(c.id);
    setShowModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateCharge(editingId, form);
        setSuccess('Charge mise à jour');
      } else {
        await createCharge(form);
        setSuccess('Charge créée avec succès');
      }
      setShowModal(false);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Supprimer cette charge ?')) return;
    await deleteCharge(id);
    loadData();
  };

  return (
    <div>
      <h1 className="page-title">Gestion des charges</h1>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <div className="card" style={{ padding: '1.2rem' }}>
          <div style={{ fontSize: '0.85rem', color: '#888' }}>Total charges</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{total.toLocaleString('fr-FR')}</div>
        </div>
        <div className="card" style={{ padding: '1.2rem' }}>
          <div style={{ fontSize: '0.85rem', color: '#888' }}>Montant total</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{formatMontant(totalMontant)}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span>Charges ({total.toLocaleString('fr-FR')})</span>
          <button className="btn btn-success" onClick={openAdd}><FiPlus /> Nouvelle charge</button>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr><th>Date</th><th>Libellé</th><th>Type</th><th>Projet</th><th>P.U.</th><th>Qté</th><th>Montant</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {charges.map(c => (
                  <tr key={c.id}>
                    <td>{c.datecharge ? new Date(c.datecharge).toLocaleDateString('fr-FR') : '-'}</td>
                    <td>{c.libelle || '-'}</td>
                    <td>{c.type_libelle ? <span className="badge badge-blue">{c.type_libelle}</span> : '-'}</td>
                    <td>{c.projet_libelle ? <span className="badge badge-green">{c.projet_libelle}</span> : '-'}</td>
                    <td>{c.prixunitaire ? new Intl.NumberFormat('fr-FR').format(c.prixunitaire) : '-'}</td>
                    <td>{c.quantite || '-'}</td>
                    <td>{c.montant ? formatMontant(c.montant) : '-'}</td>
                    <td>
                      <div className="actions">
                        <button className="btn-icon btn-warning" onClick={() => openEdit(c)}><FiEdit2 /></button>
                        <button className="btn-icon btn-danger" onClick={() => handleDelete(c.id)}><FiTrash2 /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              {page > 1 && <button onClick={() => setPage(page - 1)}>&laquo;</button>}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const p = Math.max(1, page - 2) + i;
                if (p > totalPages) return null;
                return <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>;
              })}
              {page < totalPages && <button onClick={() => setPage(page + 1)}>&raquo;</button>}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingId ? 'Modifier la charge' : 'Nouvelle charge'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}><FiX /></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group"><label>Libellé</label><input className="form-input" value={form.libelle} onChange={e => setForm({ ...form, libelle: e.target.value })} required /></div>
                  <div className="form-group"><label>Date</label><input type="date" className="form-input" value={form.datecharge} onChange={e => setForm({ ...form, datecharge: e.target.value })} required /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Type de charge</label>
                    <select className="form-input" value={form.typecharge_id} onChange={e => setForm({ ...form, typecharge_id: e.target.value })} required>
                      <option value="">--</option>
                      {typecharges.map(t => <option key={t.id} value={t.id}>{t.libelle}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label>Projet</label>
                    <select className="form-input" value={form.projet_id} onChange={e => setForm({ ...form, projet_id: e.target.value })}>
                      <option value="">--</option>
                      {projets.map((p: any) => <option key={p.id} value={p.id}>{p.libelle}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Prix unitaire</label><input type="number" className="form-input" value={form.prixunitaire} onChange={e => setForm({ ...form, prixunitaire: e.target.value })} /></div>
                  <div className="form-group"><label>Quantité</label><input type="number" className="form-input" value={form.quantite} onChange={e => setForm({ ...form, quantite: e.target.value })} /></div>
                </div>
                <div className="form-group"><label>Description</label><textarea className="form-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} /></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Charges;
